import { useState, useEffect, useCallback, useRef } from 'react'
import type { Period } from './types'

const BASE = '/api'
let TOKEN = localStorage.getItem('cc-token') ?? ''

export function setToken(t: string) {
  TOKEN = t
  localStorage.setItem('cc-token', t)
}

/* Auto-configure le token depuis /setup au boot — toujours, pour éviter
   un token périmé en localStorage qui empêcherait toute requête */
export async function autoSetup(): Promise<boolean> {
  try {
    const res = await fetch(`${BASE}/setup`)
    if (!res.ok) return false
    const data = await res.json() as { token?: string }
    if (data.token && data.token !== TOKEN) setToken(data.token)
    return true
  } catch {
    return false
  }
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    headers: { Authorization: `Bearer ${TOKEN}` },
  })
  if (!res.ok) throw new Error(`${res.status} ${path}`)
  return res.json() as Promise<T>
}

async function post<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${TOKEN}` },
  })
  if (!res.ok) throw new Error(`${res.status} ${path}`)
  return res.json() as Promise<T>
}

/* ── useQuery avec backoff exponentiel en cas d'erreur ────────── */

interface UseQueryResult<T> {
  data: T | null
  loading: boolean
  error: string | null
  connected: boolean
  refetch: () => void
}

export function useQuery<T>(path: string, pollMs = 0): UseQueryResult<T> {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [connected, setConnected] = useState(true)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const retryRef = useRef(0)
  const mountedRef = useRef(true)

  const schedule = useCallback((delayMs: number, fn: () => void) => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(fn, delayMs)
  }, [])

  const fetch_ = useCallback(() => {
    if (!mountedRef.current) return
    setLoading(true)
    get<T>(path)
      .then((d) => {
        if (!mountedRef.current) return
        setData(d)
        setError(null)
        setConnected(true)
        retryRef.current = 0
        if (pollMs > 0) schedule(pollMs, fetch_)
      })
      .catch((e: Error) => {
        if (!mountedRef.current) return
        setError(e.message)
        setConnected(false)
        /* Backoff exponentiel : 2s → 4s → 8s → … max 60s */
        retryRef.current = Math.min(retryRef.current + 1, 5)
        const delay = Math.min(2000 * Math.pow(2, retryRef.current - 1), 60_000)
        schedule(delay, fetch_)
      })
      .finally(() => {
        if (mountedRef.current) setLoading(false)
      })
  }, [path, pollMs, schedule])

  useEffect(() => {
    mountedRef.current = true
    fetch_()
    return () => {
      mountedRef.current = false
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [fetch_])

  return { data, loading, error, connected, refetch: fetch_ }
}

/* ── useSync : état global de connexion + touche R ────────────── */

export interface SyncState {
  connected: boolean
  lastSync: Date | null
  forceSync: () => void
}

let syncListeners: Array<() => void> = []

export function registerSyncListener(fn: () => void): () => void {
  syncListeners.push(fn)
  return () => { syncListeners = syncListeners.filter((l) => l !== fn) }
}

export function triggerForceSync(): void {
  syncListeners.forEach((fn) => fn())
}

/* ── Endpoints spécifiques ────────────────────────────────────── */

export function useHealth() {
  return useQuery<{
    status: string; watchedProjects: number; watcherReady: boolean
    totalReports: number; openAlerts: number
  }>('/health', 30_000)
}

export function useProjects() {
  return useQuery<{ projects: ProjectSummary[] }>('/projects', 30_000)
}

export function useAggregate(period: Period) {
  return useQuery<{ buckets: Bucket[] }>(`/aggregate?period=${period}`, 30_000)
}

export function useTimeline(limit = 50) {
  return useQuery<{ reports: Report[]; nextCursor: string | null }>(`/timeline?limit=${limit}`)
}

export function useKanban() {
  return useQuery<KanbanData>('/kanban', 30_000)
}

export function useAlerts() {
  return useQuery<{ alerts: Alert[] }>('/alerts', 30_000)
}

export function useTasks(projectId: string | null) {
  return useQuery<{ projectId: string; tasks: ProjectTask[]; count: number; source: string }>(
    projectId ? `/tasks/${projectId}` : '/health',
    60_000,
  )
}

export interface ProjectTask {
  id: string
  title: string
  status: 'done' | 'in_progress' | 'blocked' | 'todo'
  priority?: string
  sprint?: string
  agent?: string
}

export function useTokens() {
  return useQuery<TokenData[]>('/tokens', 5 * 60_000)
}

export interface TokenData {
  projectId: string
  consumed: number
  estimated: number
  taskPercent: number
  hasBudgetFile: boolean
}

export async function ackAlert(id: string) {
  return post<{ success: boolean }>(`/alerts/${id}/ack`)
}

export function exportCsvUrl(period: Period) {
  return `${BASE}/export.csv?period=${period}`
}

/* Types inline */
export interface ProjectSummary {
  id: string; name: string; lastActivity: string | null
  actions: number; errors: number; completed: number; commits: number
}
export interface Bucket {
  projectId: string; period: string; bucketKey: string
  totals: {
    actions: number; errors: number; inProgress: number; completed: number
    successRate: number; timeSeconds: number; filesModified: number; commits: number
  }
  trend: number; health: 'good' | 'warning' | 'critical'
}
export interface Report {
  id: string; projectId: string; agent: string; branch: string
  startedAt: string; endedAt: string; status: string
  metrics: {
    actions: number; errors: number; inProgress: number; completed: number
    successRate: number; timeSeconds: number; filesModified: number; commits: number
  }
  tasks: { id: string; title: string; state: string }[]
  issues: { severity: string; message: string; at: string }[]
  summaryMd: string
}
export interface Alert {
  id: string; projectId: string; severity: string
  message: string; at: string; ackedAt?: string | null
}
export interface KanbanData {
  inProgress: { projectId: string; title: string; state: string; at: string }[]
  done: { projectId: string; title: string; state: string; at: string }[]
  errors: { projectId: string; title: string; state: string; at: string }[]
}
